import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { format } from "date-fns";
import { CheckCircle2, X } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AuditLogCard } from "@/components/audit-log-card";
import { DynamicFormSections } from "@/components/DynamicFormSections";
import { PageContainer } from "@/components/page-container";
import { FormErrorAlert } from "@/components/stone/form-errors";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Heading } from "@/components/ui/typography";
import { getErrorMessage } from "@/lib/form-errors";
import type { TemplateBlocks } from "@/lib/form-render-helpers";
import {
  getAuthorisationDetails,
  getFormDisplayValues,
  getMonitoringDetails,
  getRejectionDetails,
  normaliseFormData,
} from "@/lib/form-utils";
import { formatDateTime, formatRoleText } from "@/lib/format";
import type { FormStatusVariant, StructuredFormData } from "@/types/form";
import { queryClient, trpc } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type ManagerFormDetailData = inferRouterOutputs<AppRouter>["forms"]["getById"];
type ManagerFormRecord = ManagerFormDetailData["form"];

type FormDetailContextType = {
  formData: ManagerFormDetailData;
  form: ManagerFormRecord;
  structured: StructuredFormData;
  displayValues: {
    statusText: string;
    status: string;
    formType: string;
    templateId: string;
    title: string;
    location: string;
    description: string;
    scheduleText: string;
    createdAtText: string | null;
    updatedAtText: string | null;
    approvedAtText: string | null;
    hasSchedule: boolean;
    formId: string;
    siteId: string;
  };
  statusVariant: FormStatusVariant;
  canApprove: boolean;
  authorisationNotes: string;
  setAuthorisationNotes: (value: string) => void;
  approvalNotes: string;
  setApprovalNotes: (value: string) => void;
  rejectionReason: string;
  setRejectionReason: (value: string) => void;
  rejectionMessage: string;
  setRejectionMessage: (value: string) => void;
  handleApprove: (callbacks?: { onSuccess?: () => void }) => void;
  handleReject: (callbacks?: { onSuccess?: () => void }) => void;
  approveMutation: { isPending: boolean };
  rejectMutation: { isPending: boolean };
  canClose: boolean;
  handleClose: (callbacks?: { onSuccess?: () => void }) => void;
  closeMutation: { isPending: boolean };
};

const FormDetailContext = createContext<FormDetailContextType | null>(null);

function useFormDetail() {
  const context = useContext(FormDetailContext);
  if (!context) {
    throw new Error("useFormDetail must be used within FormDetailProvider");
  }
  return context;
}

function WorkSummaryCard() {
  const { displayValues } = useFormDetail();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Work summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="space-y-1">
          <Label className="text-sm font-medium">Title</Label>
          <p className="text-sm text-muted-foreground">{displayValues.title}</p>
        </div>
        <div className="space-y-1">
          <Label className="text-sm font-medium">Description</Label>
          <p className="text-sm text-muted-foreground">
            {displayValues.description}
          </p>
        </div>
        {/* <div className="space-y-1">
          <Label className="text-sm font-medium">Location</Label>
          <p className="text-sm text-muted-foreground">
            {displayValues.location}
          </p>
        </div> */}
        {displayValues.hasSchedule && (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Schedule</Label>
            <p className="text-sm text-muted-foreground">
              {displayValues.scheduleText}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
function PeopleInvolvedCard() {
  const { structured } = useFormDetail();

  const formatPersonName = (record: Record<string, unknown>) => {
    const name = typeof record.name === "string" ? record.name.trim() : "";
    if (name.length > 0) return name;
    if (typeof record.label === "string" && record.label.trim().length > 0) {
      return record.label as string;
    }
    if (typeof record.role === "string" && record.role.trim().length > 0) {
      return formatRoleText(record.role as string);
    }
    return "Person";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>People involved</CardTitle>
      </CardHeader>
      <CardContent>
        {structured.people.length > 0 ? (
          <div className="space-y-4 text-sm">
            {structured.people.map((record, idx) => {
              const signedAt = record.signed_at
                ? new Date(record.signed_at as string)
                : null;

              return (
                <div
                  key={`${record.name ?? "person"}-${idx}`}
                  className="space-y-1"
                >
                  <p className="font-medium text-foreground">
                    {formatPersonName(record)}
                  </p>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>
                      Signed:{" "}
                      {signedAt ? format(signedAt, "PPp") : "Not signed"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No people have been added yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function HazardsControlsCard() {
  const { structured } = useFormDetail();

  const getRiskVariant = (riskValue: unknown) => {
    if (typeof riskValue !== "string") return "gray";
    const normalised = riskValue.toLowerCase();
    return ["high", "medium", "low"].includes(normalised)
      ? (normalised as "high" | "medium" | "low")
      : "gray";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          Hazards & controls
        </CardTitle>
      </CardHeader>
      <CardContent>
        {structured.hazards.length > 0 ? (
          <div className="space-y-4 text-sm">
            {structured.hazards.map((record, idx) => {
              return (
                <div
                  key={`${record.name ?? "hazard"}-${idx}`}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground flex items-center gap-2">
                      {Boolean(record.risk) && (
                        <Badge size="sm" variant={getRiskVariant(record.risk)}>
                          {record.risk as string}
                        </Badge>
                      )}
                      {typeof record.name === "string" ? record.name : "Hazard"}
                    </p>
                  </div>
                  {Array.isArray(record.controls) &&
                  (record.controls as unknown[]).length > 0 ? (
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Controls:</p>
                      <ul className="ml-4 space-y-1">
                        {(record.controls as unknown[]).map(
                          (control, controlIdx) => (
                            <li
                              key={`${record.name ?? "hazard"}-${idx}-control-${controlIdx}`}
                              className="text-sm text-muted-foreground flex items-start gap-2"
                            >
                              <span className="text-muted-foreground/60 mt-0.5">
                                •
                              </span>
                              {control as string}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  ) : record.controls ? (
                    <p className="text-sm text-muted-foreground">
                      Controls: {JSON.stringify(record.controls)}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No hazards have been recorded yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AuthorisationCard() {
  const { structured, displayValues } = useFormDetail();

  const authorisation = getAuthorisationDetails(structured);

  if (!authorisation) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Authorisation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label className="text-sm font-medium">Level</Label>
          <p className="text-sm text-muted-foreground capitalize">
            {authorisation.level}
          </p>
        </div>
        {authorisation.notes ? (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Notes</Label>
            <p className="text-sm text-muted-foreground">
              {authorisation.notes}
            </p>
          </div>
        ) : null}
        {authorisation.approvedByUserId ? (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Approved By</Label>
            <p className="text-sm text-muted-foreground">
              {authorisation.approvedByUserId}
            </p>
          </div>
        ) : null}
        {displayValues.approvedAtText && (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Approved At</Label>
            <p className="text-sm text-muted-foreground">
              {displayValues.approvedAtText}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MonitoringCard() {
  const { structured, displayValues } = useFormDetail();

  const monitoring = getMonitoringDetails(structured);

  if (!monitoring) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monitoring</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {monitoring.updates.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Updates</Label>
            <div className="space-y-2">
              {monitoring.updates.map((update) => {
                return (
                  <div key={update.key} className="text-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Update</span>
                      {update.at && (
                        <span className="text-muted-foreground">
                          {format(update.at, "PPp")}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground">{update.note}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {monitoring.nextCheck && displayValues.status !== "Closed" && (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Next Check</Label>
            <p className="text-sm text-muted-foreground">
              {format(monitoring.nextCheck, "PPpp")}
            </p>
          </div>
        )}

        {monitoring.closedAt && (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Closed At</Label>
            <p className="text-sm text-muted-foreground">
              {format(monitoring.closedAt, "PPpp")}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RejectionCard() {
  const { form } = useFormDetail();
  const rejection = getRejectionDetails(form);

  if (!rejection) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rejection</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label className="text-sm font-medium">Reason</Label>
          <p className="text-sm text-muted-foreground">{rejection.reason}</p>
        </div>
        {rejection.message ? (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Message</Label>
            <p className="text-sm text-muted-foreground">{rejection.message}</p>
          </div>
        ) : null}
        {rejection.rejectedByUserId ? (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Rejected By</Label>
            <p className="text-sm text-muted-foreground">
              {rejection.rejectedByUserId}
            </p>
          </div>
        ) : null}
        {rejection.rejectedAt ? (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Rejected At</Label>
            <p className="text-sm text-muted-foreground">
              {formatDateTime(rejection.rejectedAt)}
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ApprovalSection() {
  const {
    canApprove,
    authorisationNotes,
    setAuthorisationNotes,
    approvalNotes,
    setApprovalNotes,
    handleApprove,
    approveMutation,
  } = useFormDetail();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleApproveClick = () => {
    handleApprove({ onSuccess: () => setIsDialogOpen(false) });
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          className="w-full"
          disabled={!canApprove || approveMutation.isPending}
        >
          <CheckCircle2 />
          Approve
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve Form</DialogTitle>
          <DialogDescription>
            Please provide approval details for this form.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="manager-authorisation">
              Approval notes (optional)
            </Label>
            <Textarea
              id="manager-authorisation"
              value={authorisationNotes}
              onChange={(event) => setAuthorisationNotes(event.target.value)}
              className="text-sm"
              placeholder="Capture specific authorisation details or controls you confirmed."
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="manager-note">
              Message to requester (optional)
            </Label>
            <Textarea
              id="manager-note"
              value={approvalNotes}
              onChange={(event) => setApprovalNotes(event.target.value)}
              className="text-sm"
              placeholder="Add context for the worker—this will appear in the audit log."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            className="w-full"
            onClick={handleApproveClick}
            disabled={approveMutation.isPending}
          >
            <CheckCircle2 />
            Approve Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectionSection() {
  const {
    canApprove,
    rejectionReason,
    setRejectionReason,
    rejectionMessage,
    setRejectionMessage,
    handleReject,
    rejectMutation,
  } = useFormDetail();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleRejectClick = () => {
    handleReject({ onSuccess: () => setIsDialogOpen(false) });
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={!canApprove || rejectMutation.isPending}
        >
          <X />
          Reject
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Form</DialogTitle>
          <DialogDescription>
            Please provide details for rejecting this form. This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rejection-reason">Rejection reason *</Label>
            <Textarea
              id="rejection-reason"
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              className="text-sm"
              placeholder="Explain why the form cannot be approved."
              rows={4}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rejection-message">
              Message to requester (optional)
            </Label>
            <Textarea
              id="rejection-message"
              value={rejectionMessage}
              onChange={(event) => setRejectionMessage(event.target.value)}
              className="text-sm"
              placeholder="Add context for the worker—this will appear in the audit log."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="destructive"
            onClick={handleRejectClick}
            disabled={!rejectionReason.trim() || rejectMutation.isPending}
          >
            <X />
            Reject Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditLogCardWrapper() {
  const { formData } = useFormDetail();

  return <AuditLogCard auditLogs={formData.auditLogs} variant="timeline" />;
}

function CloseSection() {
  const { canClose, handleClose, closeMutation } = useFormDetail();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={!canClose || closeMutation.isPending}
        >
          Close Form
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Close this form?</AlertDialogTitle>
          <AlertDialogDescription>
            This marks the permit as completed and closed. This action cannot be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() =>
              handleClose({ onSuccess: () => setIsDialogOpen(false) })
            }
            disabled={closeMutation.isPending}
          >
            Close Form
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ManagerActionCard() {
  const { canApprove, canClose } = useFormDetail();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manager action</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <ApprovalSection />
        <RejectionSection />
        {canClose ? <CloseSection /> : null}
        {!canApprove && !canClose ? (
          <p className="text-sm text-muted-foreground text-center px-6">
            Only forms awaiting review can be approved or rejected.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function FormHeader() {
  const { displayValues, statusVariant } = useFormDetail();

  return (
    <div className="flex flex-col gap-3 border-b pb-6">
      <Heading variant="page">{displayValues.title}</Heading>
      <div className="flex items-center gap-2">
        <Badge variant={statusVariant}>{displayValues.statusText}</Badge>
        {displayValues.formType && (
          <Badge variant="outline">{displayValues.formType}</Badge>
        )}
      </div>
    </div>
  );
}

function FormMeta() {
  const { displayValues, formData } = useFormDetail();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground">
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">Form ID</div>
          <div className="break-all">{displayValues.formId}</div>
        </div>
        {displayValues.templateId && (
          <div className="space-y-0.5">
            <div className="font-medium text-foreground">Template</div>
            <div>{displayValues.templateId}</div>
          </div>
        )}
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">Site</div>
          <div>{formData.site?.name ?? displayValues.siteId}</div>
        </div>
        {displayValues.createdAtText && (
          <div className="space-y-0.5">
            <div className="font-medium text-foreground">Created</div>
            <div>{displayValues.createdAtText}</div>
          </div>
        )}
        {displayValues.updatedAtText && (
          <div className="space-y-0.5">
            <div className="font-medium text-foreground">Updated</div>
            <div>{displayValues.updatedAtText}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ManagerFormDetail() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const formQuery = useQuery(
    trpc.forms.getById.queryOptions({ id: formId ?? "" })
  );
  const formData = formQuery.data;
  const form = formData?.form;

  const [approvalNotes, setApprovalNotes] = useState("");
  const [authorisationNotes, setAuthorisationNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionMessage, setRejectionMessage] = useState("");

  useEffect(() => {
    if (!formData) return;
    setApprovalNotes("");
    setAuthorisationNotes("");
    setRejectionReason("");
    setRejectionMessage("");
  }, [formData]);

  const approveMutation = useMutation({
    ...trpc.forms.approve.mutationOptions(),
    onSuccess: async () => {
      toast.success("Form approved");
      await queryClient.invalidateQueries({
        queryKey: trpc.forms.getById.queryOptions({ id: formId ?? "" })
          .queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: trpc.forms.listBySite.queryKey(),
      });
      setApprovalNotes("");
      setAuthorisationNotes("");
    },
    onError: (error) => toast.error(error.message),
  });

  const rejectMutation = useMutation({
    ...trpc.forms.reject.mutationOptions(),
    onSuccess: async () => {
      toast.success("Form rejected");
      await queryClient.invalidateQueries({
        queryKey: trpc.forms.getById.queryOptions({ id: formId ?? "" })
          .queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: trpc.forms.listBySite.queryKey(),
      });
      setRejectionReason("");
      setRejectionMessage("");
    },
    onError: (error) => toast.error(error.message),
  });

  const closeMutation = useMutation({
    ...trpc.forms.close.mutationOptions(),
    onSuccess: async () => {
      toast.success("Form closed");
      await queryClient.invalidateQueries({
        queryKey: trpc.forms.getById.queryOptions({ id: formId ?? "" })
          .queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: trpc.forms.listBySite.queryOptions({
          siteId: form?.siteId ?? "",
        }).queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: trpc.forms.listBySite.queryOptions({}).queryKey,
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const structured = useMemo(() => normaliseFormData(form), [form]);

  // Memoized display values for better readability and performance
  const displayValues = useMemo(
    () =>
      getFormDisplayValues({
        form,
        structured,
        templateName: formData?.template?.name,
      }),
    [form, structured, formData?.template?.name]
  );

  const canApprove = form?.status === "pending_review";
  const canClose = form?.status === "approved";

  const handleClose = (callbacks?: { onSuccess?: () => void }) => {
    if (!form) return;
    closeMutation.mutate(
      { formId: form.id },
      { onSuccess: () => callbacks?.onSuccess?.() }
    );
  };

  const handleApprove = (callbacks?: { onSuccess?: () => void }) => {
    if (!form) return;
    approveMutation.mutate(
      {
        formId: form.id,
        note: approvalNotes.trim() || undefined,
        authorisation: {
          notes: authorisationNotes.trim() || undefined,
        },
      },
      { onSuccess: () => callbacks?.onSuccess?.() }
    );
  };

  const handleReject = (callbacks?: { onSuccess?: () => void }) => {
    if (!form) return;
    rejectMutation.mutate(
      {
        formId: form.id,
        rejectionReason: rejectionReason.trim(),
        message: rejectionMessage.trim() || undefined,
      },
      { onSuccess: () => callbacks?.onSuccess?.() }
    );
  };

  if (!formId) return null;

  if (formQuery.isLoading) {
    return (
      <div className="flex h-full flex-col bg-background">
        <div className="border-b p-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="mt-3 h-4 w-48" />
        </div>
        <div className="flex-1 space-y-4 p-4">
          <Skeleton className="h-36 rounded-lg" />
          <Skeleton className="h-36 rounded-lg" />
        </div>
      </div>
    );
  }

  if (formQuery.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-background p-6">
        <FormErrorAlert
          message={getErrorMessage(formQuery.error, "Failed to load form.")}
        />
      </div>
    );
  }

  if (!form || !formData) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-background p-6">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Form not found</EmptyTitle>
            <EmptyDescription>
              This form is no longer available or you don't have permission to
              view it.
            </EmptyDescription>
          </EmptyHeader>
          <Button className="mt-4" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Empty>
      </div>
    );
  }

  const statusVariant = (form?.status || "draft") as FormStatusVariant;

  const contextValue: FormDetailContextType = {
    formData,
    form,
    structured,
    displayValues,
    statusVariant,
    canApprove,
    authorisationNotes,
    setAuthorisationNotes,
    approvalNotes,
    setApprovalNotes,
    rejectionReason,
    setRejectionReason,
    rejectionMessage,
    setRejectionMessage,
    handleApprove,
    handleReject,
    approveMutation,
    rejectMutation,
    canClose,
    handleClose,
    closeMutation,
  };

  return (
    <FormDetailContext.Provider value={contextValue}>
      <PageContainer className="flex min-h-full flex-col gap-6 lg:gap-8">
        <FormHeader />

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-start">
          <section className="space-y-4">
            <WorkSummaryCard />
            <DynamicFormSections
              templateBlocks={
                formData?.template?.blocks as TemplateBlocks | null
              }
              formBlocks={structured.blocks}
              canEdit={false}
            />
            <PeopleInvolvedCard />
            <HazardsControlsCard />
            <AuthorisationCard />
            <RejectionCard />
            <MonitoringCard />
            <AuditLogCardWrapper />
          </section>

          <aside className="space-y-4 xl:sticky xl:top-24">
            <ManagerActionCard />
            <FormMeta />
          </aside>
        </div>
      </PageContainer>
    </FormDetailContext.Provider>
  );
}
